// Generic building blocks for a "2-level" ECharts tooltip: a plain flat list
// when there is nothing to sub-total, or side-by-side columns split by a
// vertical divider (each with its own subtotal) when a group has multiple
// rows. Pass a declarative `series` catalog so row order, labels, colors,
// and "NA" display for visible-but-null values are handled in one place.

import { dealTypeValue } from './useChartOptions';

const TOTAL_LABEL_SERIES_PREFIX = '__';
const TOTAL_LABEL_SERIES_SUFFIX = '_total_label__';
const NULL_DISPLAY_VALUE = 'NA';

// Matches the invisible helper series injected by appendStackedTotalLabelSeries
// (utils/stackedTotalLabel.ts) so it never leaks into tooltip content.
export const isTotalLabelSeries = (seriesName?: string): boolean => (
  Boolean(seriesName)
  && Boolean(seriesName?.startsWith(TOTAL_LABEL_SERIES_PREFIX))
  && Boolean(seriesName?.endsWith(TOTAL_LABEL_SERIES_SUFFIX))
);

// Shared convention across this module's tooltips: a data item can override
// its displayed value via `tooltipValue`, falling back to the raw series value.
// ECharts may pass a gradient object as item.color when bars use itemStyle.color;
// prefer an explicit solid markerColor on the data point (same pattern as
// Overall Financial Profit Trend).
export const resolveTooltipMarkerColor = (item: any): string | undefined => {
  if (item?.data?.markerColor) {
    return item.data.markerColor;
  }
  if (typeof item?.color === 'string') {
    return item.color;
  }
  return undefined;
};

export const resolveTooltipValue = (item: any): number | null => {
  const rawValue = item?.data?.tooltipValue ?? item?.value;
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return null;
  }
  const numericValue = Number(rawValue);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const defaultGetRowKey = (item: any): string | undefined => (
  item?.data?.chartKey ?? item?.seriesName
);

// Buckets raw ECharts axis-trigger tooltip params into ordered groups.
// Legend-hidden series are omitted from params and therefore excluded here.
export const groupTooltipParams = <TGroupKey extends string>(
  params: any,
  {
    getGroupKey,
    groupOrder,
  }: {
    getGroupKey: (item: any) => TGroupKey | undefined;
    groupOrder: readonly TGroupKey[];
  },
): { headerName: string; groups: [TGroupKey, any[]][] } => {
  const tooltipParams = Array.isArray(params) ? params : [params];
  const visibleParams = tooltipParams.filter(
    (item) => !isTotalLabelSeries(item?.seriesName),
  );

  const headerName = visibleParams[0]?.name || '';
  const groups = groupOrder
    .map((groupKey): [TGroupKey, any[]] => [
      groupKey,
      visibleParams.filter((item) => getGroupKey(item) === groupKey),
    ])
    .filter(([, items]) => items.length > 0);

  return { headerName, groups };
};

export interface ITooltipRow {
  label: string;
  value: string;
  markerColor?: string;
}

export interface ITooltipColumn {
  // Omit to render this column's rows without a subtotal line.
  totalLabel?: string;
  totalValue?: string;
  rows: ITooltipRow[];
}

export interface IGroupedChartTooltipOptions {
  headerLeft: string;
  headerRight: string;
  columns: ITooltipColumn[];
  /** Optional top line (e.g. Portfolio studio name above the date header). */
  title?: string;
  // Width applied to each column only when the multi-column layout kicks in.
  columnWidth?: string;
  // Width applied when every column collapses into a single flat list.
  flatMinWidth?: string;
}

// Text lines use this fixed line-height so the marker's centering math below
// is exact rather than guessed — without pinning it, an inherited page
// line-height would silently throw off the marker/text vertical alignment.
const TEXT_LINE_HEIGHT = 14;
const MARKER_SIZE = 8;
const MARKER_TOP_OFFSET = (TEXT_LINE_HEIGHT - MARKER_SIZE) / 2;

const buildMarker = (color?: string): string => (
  // margin-top centers the fixed-size marker against the text's first line
  // (align-items:flex-start on the row keeps it pinned to the top rather
  // than centering against the full wrapped block height).
  color
    ? `<span style="display:inline-block;width:${MARKER_SIZE}px;height:${MARKER_SIZE}px;border-radius:50%;background:${color};margin-right:8px;margin-top:${MARKER_TOP_OFFSET}px;flex-shrink:0;"></span>`
    : ''
);

const buildHeaderRow = (headerLeft: string, headerRight: string): string => (
  `<div style="display:flex;justify-content:space-between;color:#D8DAE4;line-height:14px;">
    <span>${headerLeft}</span>
    <span>${headerRight}</span>
  </div>`
);

const buildRow = (row: ITooltipRow): string => (
  // Text sits in its own flex column (min-width:0 lets it shrink/wrap) next
  // to the marker, rather than flowing inline after it — so wrapped lines
  // align under the first line of text instead of restarting under the marker.
  `<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:30px;">
    <span style="display:flex;flex:1;min-width:0;color:#EBECF1;line-height:${TEXT_LINE_HEIGHT}px;">
      ${buildMarker(row.markerColor)}
      <span style="min-width:0;word-break:break-word;">${row.label}</span>
    </span>
    <span style="font-size:14px;font-weight:700;color:#EBECF1;flex-shrink:0;line-height:${TEXT_LINE_HEIGHT}px;">${row.value}</span>
  </div>`
);

const buildColumn = (
  column: ITooltipColumn,
  headerLeft: string,
  headerRight: string,
  columnWidth: string,
): string => (
  `<div style="width:${columnWidth};max-width:${columnWidth};flex-shrink:0;box-sizing:border-box;display:flex;flex-direction:column;gap:10px;">
    ${buildHeaderRow(headerLeft, headerRight)}
    ${column.totalLabel ? `<div style="display:flex;justify-content:space-between;">
      <span style="color:#D8DAE4;">${column.totalLabel}</span>
      <span style="font-size:14px;font-weight:700;color:#EBECF1;">${column.totalValue}</span>
    </div>` : ''}
    ${column.rows.map(buildRow).join('')}
  </div>`
);

// Auto-selects the layout: a single column with more than one row anywhere
// switches every column into its own side-by-side block (with subtotal and a
// divider); otherwise all rows render as one plain flat list.
export const buildGroupedChartTooltip = ({
  headerLeft,
  headerRight,
  columns,
  title,
  columnWidth = '208px',
  flatMinWidth = '150px',
}: IGroupedChartTooltipOptions): string => {
  const nonEmptyColumns = columns.filter((column) => column.rows.length > 0);
  if (!nonEmptyColumns.length) {
    return '';
  }

  const needsGroupedLayout = nonEmptyColumns.some((column) => column.rows.length > 1);
  const titleHtml = title
    ? `<div style="color:#EBECF1;word-break:break-word;line-height:14px;">${title}</div>`
    : '';

  // ECharts' tooltip content container hardcodes white-space:nowrap, which
  // would otherwise be inherited and silently defeat every wrapping rule
  // below regardless of width — override it back to normal at the root.
  if (!needsGroupedLayout) {
    const rows = nonEmptyColumns.flatMap((column) => column.rows);
    return `<div style="min-width:${flatMinWidth};font-size:12px;line-height:14px;white-space:normal;display:flex;flex-direction:column;gap:10px;">
      ${titleHtml}
      ${buildHeaderRow(headerLeft, headerRight)}
      ${rows.map(buildRow).join('')}
    </div>`;
  }

  const divider = '<div style="width:1px;align-self:stretch;background-color:rgba(216,218,228,0.3);margin:0 16px;"></div>';
  const columnsHtml = nonEmptyColumns.map((column) => (
    buildColumn(column, headerLeft, headerRight, columnWidth)
  ));

  return `<div style="font-size:12px;line-height:14px;white-space:normal;display:flex;flex-direction:column;gap:10px;">
    ${titleHtml}
    <div style="display:flex;">
      ${columnsHtml.join(divider)}
    </div>
  </div>`;
};

export interface IGroupedChartTooltipSeries<TGroupKey extends string> {
  groupKey: TGroupKey;
  rowKey: string;
  label: string;
  markerColor?: string;
}

export interface IGroupedChartTooltipFormatterConfig<TGroupKey extends string> {
  // Declarative row catalog: drives group layout, row order, labels, colors.
  // Legend-hidden rows are omitted; visible rows with null values show "NA".
  series: readonly IGroupedChartTooltipSeries<TGroupKey>[];
  groupOrder: readonly TGroupKey[];
  /**
   * Fallback header-right text when the hovered point has no tooltipHeaderRight.
   * Prefer setting tooltipHeaderRight on each data item (Actual / Forecast).
   */
  headerRight?: string;
  // Column omits its subtotal row when its group key has no title here.
  groupTotalTitles?: Partial<Record<TGroupKey, string>>;
  // Match ECharts tooltip params to catalog rows; defaults to chartKey/seriesName.
  getRowKey?: (item: any) => string | undefined;
  // Passed straight to dealTypeValue (e.g. 'num', 'percent'); defaults to 'num'.
  valueType?: string;
  columnWidth?: string;
  flatMinWidth?: string;
}

const formatTooltipValue = (
  valueType: string,
  rawValue: number | null,
): string => {
  if (rawValue === null) {
    return NULL_DISPLAY_VALUE;
  }
  return dealTypeValue(valueType, rawValue) ?? NULL_DISPLAY_VALUE;
};

const resolveGroupTotalValue = (
  groupSeries: readonly IGroupedChartTooltipSeries<string>[],
  paramsByRowKey: Map<string, any>,
): number | null => {
  let sum = 0;
  let hasValue = false;

  for (const entry of groupSeries) {
    const matchedParam = paramsByRowKey.get(entry.rowKey);
    const rawValue = matchedParam ? resolveTooltipValue(matchedParam) : null;
    if (rawValue === null) {
      continue;
    }
    sum += rawValue;
    hasValue = true;
  }

  return hasValue ? sum : null;
};

// Turns an ECharts tooltip.formatter from a declarative series catalog.
export const buildGroupedChartTooltipFormatter = <TGroupKey extends string>({
  series,
  groupOrder,
  headerRight,
  groupTotalTitles = {},
  getRowKey = defaultGetRowKey,
  valueType = 'num',
  columnWidth,
  flatMinWidth,
}: IGroupedChartTooltipFormatterConfig<TGroupKey>) => (params: any) => {
  const tooltipParams = Array.isArray(params) ? params : [params];
  const visibleParams = tooltipParams.filter(
    (item) => !isTotalLabelSeries(item?.seriesName),
  );
  // Optional tooltip title (e.g. Portfolio studio_name). Absent → Studios-style header only.
  const tooltipTitle = visibleParams[0]?.data?.title;
  const headerName = tooltipTitle
    ? (visibleParams[0]?.data?.date || '')
    : (visibleParams[0]?.name || '');
  // Per-point Actual/Forecast (Headcount source / Burn Rate is_actual) wins over static config.
  const resolvedHeaderRight = visibleParams[0]?.data?.tooltipHeaderRight
    ?? headerRight
    ?? '';

  const paramsByRowKey = new Map<string, any>();
  visibleParams.forEach((item) => {
    const rowKey = getRowKey(item);
    if (rowKey) {
      paramsByRowKey.set(rowKey, item);
    }
  });

  const columns: ITooltipColumn[] = groupOrder.reduce<ITooltipColumn[]>(
    (acc, groupKey) => {
      const groupSeries = series.filter((entry) => entry.groupKey === groupKey);
      if (!groupSeries.length) {
        return acc;
      }

      const rows: ITooltipRow[] = groupSeries.reduce<ITooltipRow[]>(
        (rowAcc, entry) => {
          const matchedParam = paramsByRowKey.get(entry.rowKey);
          // Legend-deselected series are absent from ECharts tooltip params.
          if (!matchedParam) {
            return rowAcc;
          }
          const rawValue = resolveTooltipValue(matchedParam);
          rowAcc.push({
            label: entry.label,
            value: formatTooltipValue(valueType, rawValue),
            markerColor: entry.markerColor ?? resolveTooltipMarkerColor(matchedParam),
          });
          return rowAcc;
        },
        [],
      );

      if (!rows.length) {
        return acc;
      }

      const total = resolveGroupTotalValue(groupSeries, paramsByRowKey);

      acc.push({
        totalLabel: groupTotalTitles[groupKey],
        totalValue: formatTooltipValue(valueType, total),
        rows,
      });
      return acc;
    },
    [],
  );

  if (!columns.length) {
    return '';
  }

  return buildGroupedChartTooltip({
    title: tooltipTitle,
    headerLeft: headerName,
    headerRight: resolvedHeaderRight,
    columns,
    columnWidth,
    flatMinWidth,
  });
};
